/**
 * LossDetector - Detects loss conditions
 * Monitors when pieces fall off the platform or tower collapses
 */

import { GAME } from '../config/gameConfig';

/**
 * Configuration for loss detection boundaries
 */
export interface LossConfig {
  /** Y position of the floor/platform */
  floorY: number;
  /** Left boundary X position */
  leftBoundary: number;
  /** Right boundary X position */
  rightBoundary: number;
  /** Extra distance below floor before loss triggers (pixels) */
  fallGrace: number;
}

/**
 * Reasons for game loss
 */
export type LossReason = 'fell' | 'collapsed' | 'out_of_bounds';

/**
 * Detects loss conditions in the game
 */
export class LossDetector {
  private config: LossConfig;

  /**
   * @param config - Loss detection configuration
   */
  constructor(config?: Partial<LossConfig>) {
    this.config = {
      floorY: config?.floorY ?? GAME.HEIGHT - 50,
      leftBoundary: config?.leftBoundary ?? -50,
      rightBoundary: config?.rightBoundary ?? GAME.WIDTH + 50,
      fallGrace: config?.fallGrace ?? 100,
    };
  }

  /**
   * Check if any loss conditions are met
   * @param placedBodies - All placed physics bodies to check
   * @param onLoss - Callback when loss is detected with reason
   * @returns true if loss detected
   */
  checkLossConditions(
    placedBodies: MatterJS.BodyType[],
    onLoss: (reason: string) => void
  ): boolean {
    for (const body of placedBodies) {
      // Check if piece fell off bottom
      if (body.position.y > this.config.floorY + this.config.fallGrace) {
        onLoss('A piece fell into the abyss!');
        return true;
      }

      // Check if piece went off left side
      if (body.position.x < this.config.leftBoundary) {
        onLoss('Tower collapsed to the left!');
        return true;
      }

      // Check if piece went off right side
      if (body.position.x > this.config.rightBoundary) {
        onLoss('Tower collapsed to the right!');
        return true;
      }
    }

    return false;
  }

  /**
   * Check if a single body is out of bounds
   * @param body - The physics body to check
   * @returns The loss reason or null if in bounds
   */
  checkBody(body: MatterJS.BodyType): LossReason | null {
    if (body.position.y > this.config.floorY + this.config.fallGrace) {
      return 'fell';
    }

    if (body.position.x < this.config.leftBoundary ||
        body.position.x > this.config.rightBoundary) {
      return 'out_of_bounds';
    }

    return null;
  }

  /**
   * Get the current configuration
   */
  getConfig(): Readonly<LossConfig> {
    return { ...this.config };
  }

  /**
   * Update configuration
   */
  updateConfig(config: Partial<LossConfig>): void {
    this.config = { ...this.config, ...config };
  }
}
